import React, { useMemo, useState } from 'react';

import { clubLogo } from '../domain/clubs';
import { ROLES } from '../domain/constants';

// Consultazione del listone: chi e' ancora libero, ordinato per valore.
//
// E' l'altra meta della pagina d'asta, e fa un lavoro diverso da PlayerSearch:
// li si cerca un nome preciso per chiamarlo quando tocca a te, qui si guarda
// cosa resta per farsi un'idea. Per questo la lista si vede senza digitare
// nulla, e cliccare una riga non chiama nessuno: apre solo la scheda.

const MAX_RESULTS = 100;

function normalise(text) {
    return (text ?? '')
        .toString()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}

function fvmOf(player) {
    const value = Number.parseFloat(player['FVM Attuale']);
    return Number.isFinite(value) ? Math.floor(value / 2) : 0;
}

export default function PlayerBrowser({ players, excludedIds, selectedId, onSelect }) {
    const [role, setRole] = useState(null);
    const [club, setClub] = useState(null);
    const [term, setTerm] = useState('');

    // I liberi si calcolano una volta sola e si tengono ordinati per valore:
    // e' l'ordine in cui si pianifica una rosa, non quello alfabetico.
    const available = useMemo(() => {
        const free = players.filter((player) => !excludedIds?.has(player.ID));
        return free.sort((a, b) => fvmOf(b) - fvmOf(a));
    }, [players, excludedIds]);

    // I due conteggi si guardano a vicenda: con l'Inter selezionata i contatori
    // di ruolo dicono quanti nerazzurri restano per ruolo, e con "P" attivo
    // ogni squadra mostra quanti portieri le restano. Un contatore che ignora
    // l'altro filtro promette righe che poi non compaiono.
    const perRole = useMemo(() => {
        const counts = { P: 0, D: 0, C: 0, A: 0 };
        available.forEach((player) => {
            if (club && player.Squadra !== club) return;
            if (counts[player.Ruolo] !== undefined) counts[player.Ruolo] += 1;
        });
        return counts;
    }, [available, club]);

    // Le squadre si ricavano dal listone, non da un elenco scritto a mano: a
    // ogni promozione l'elenco si aggiorna da solo, e una squadra rimasta senza
    // giocatori liberi sparisce dai filtri invece di offrire una lista vuota.
    const clubs = useMemo(() => {
        const counts = new Map();
        available.forEach((player) => {
            if (role && player.Ruolo !== role) return;
            counts.set(player.Squadra, (counts.get(player.Squadra) ?? 0) + 1);
        });
        return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0], 'it'));
    }, [available, role]);

    // La ricerca testuale guarda solo il nome: la squadra ha i suoi bottoni, e
    // finche' cercava anche li' digitare "Como" nascondeva i giocatori che si
    // chiamano cosi' in mezzo a tutta la rosa comasca.
    const filtered = useMemo(() => {
        const needle = normalise(term.trim());
        return available.filter((player) => {
            if (role && player.Ruolo !== role) return false;
            if (club && player.Squadra !== club) return false;
            if (!needle) return true;
            return (
                normalise(player.Cognome).includes(needle) ||
                normalise(player['Nome Completo']).includes(needle)
            );
        });
    }, [available, role, club, term]);

    const shown = filtered.slice(0, MAX_RESULTS);

    return (
        <div className="panel">
            <div className="panel-header">
                <h2 className="panel-title">Listone</h2>
                <span className="u-dim">{filtered.length} liberi</span>
            </div>
            <div className="panel-body browser">
                <div className="btn-group" role="group" aria-label="Filtra per ruolo">
                    <button
                        type="button"
                        className={`btn btn--sm ${role === null ? 'btn--primary' : 'btn--ghost'}`}
                        onClick={() => setRole(null)}
                    >
                        Tutti
                    </button>
                    {ROLES.map((value) => (
                        <button
                            key={value}
                            type="button"
                            className={`btn btn--sm ${role === value ? 'btn--primary' : 'btn--ghost'}`}
                            onClick={() => setRole(role === value ? null : value)}
                        >
                            {value} <span className="u-dim">{perRole[value]}</span>
                        </button>
                    ))}
                </div>

                <div className="btn-group browser-clubs" role="group" aria-label="Filtra per squadra">
                    <button
                        type="button"
                        className={`btn btn--sm ${club === null ? 'btn--primary' : 'btn--ghost'}`}
                        onClick={() => setClub(null)}
                    >
                        Tutte
                    </button>
                    {clubs.map(([name, count]) => {
                        const logo = clubLogo(name);
                        return (
                            <button
                                key={name}
                                type="button"
                                // Lo stemma e' l'etichetta: venti nomi scritti
                                // occupano mezzo pannello, venti stemmi una riga
                                // e mezza, e sono comunque piu' veloci da
                                // riconoscere. Il nome resta per chi non vede
                                // l'immagine e nel suggerimento del puntatore.
                                className={`btn btn--sm browser-club ${
                                    club === name ? 'btn--primary' : 'btn--ghost'
                                }`}
                                title={`${name} — ${count} liberi`}
                                aria-label={`${name}, ${count} liberi`}
                                aria-pressed={club === name}
                                onClick={() => setClub(club === name ? null : name)}
                            >
                                {logo ? <img src={logo} alt="" /> : name}
                            </button>
                        );
                    })}
                </div>

                <input
                    type="text"
                    className="input search-field"
                    placeholder="Filtra per nome..."
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                />

                <div className="search-results">
                    {shown.length === 0 && (
                        <div className="search-empty">Nessun giocatore libero con questi filtri.</div>
                    )}
                    {shown.map((player) => (
                        <button
                            key={player.ID}
                            type="button"
                            className={`search-item browser-item ${
                                player.ID === selectedId ? 'is-selected' : ''
                            }`}
                            onClick={() => onSelect(player)}
                        >
                            <span className="search-item-name">{player['Nome Completo']}</span>
                            <span className="search-item-meta">
                                <span className="badge badge--role" data-role={player.Ruolo}>
                                    {player.Ruolo}
                                </span>
                                {player.Squadra}
                                <span className="num browser-fvm">{fvmOf(player)}</span>
                            </span>
                        </button>
                    ))}
                    {filtered.length > MAX_RESULTS && (
                        <div className="search-empty">
                            Mostrati i primi {MAX_RESULTS} di {filtered.length}: restringi i filtri.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
